import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Chip
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useApi } from '../contexts/ApiContext';

const LocationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnState = location.state || {};
  const [locationData, setLocationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getLocationDetails } = useApi();

  // Handle back navigation - preserve expanded state
  const handleBack = useCallback(() => {
    const { returnSearch, returnPage, returnExpandedPanels } = returnState;
    const locationName = decodeURIComponent(id);

    // Auto-expand this location when returning
    const expandedPanels = { ...(returnExpandedPanels || {}) };
    expandedPanels[locationName] = true;

    navigate('/terminals', {
      state: {
        preserveSearch: true,
        searchTerm: returnSearch || '',
        page: returnPage || 0,
        expandedPanels,
        expandedStoreName: locationName
      }
    });
  }, [navigate, returnState, id]);

  useEffect(() => {
    if (id) {
      loadLocationDetails();
    }
  }, [id]);

  const loadLocationDetails = async () => {
    try {
      setLoading(true);
      const locationName = decodeURIComponent(id);
      const response = await getLocationDetails(locationName);
      setLocationData(response);
    } catch (err) {
      setError(err.message || 'Failed to load location details');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          {error}
        </Alert>
        <Box mt={2}>
          <Button startIcon={<ArrowBack />} onClick={handleBack}>
            Back to Terminals
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Back to Terminals
          </Button>
          <Typography variant="h4">
            {locationData?.locationName || locationData?.businessName}
          </Typography>
        </Box>
      </Box>

      {/* Terminals Overview */}
      {locationData?.terminals && locationData.terminals.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Associated Terminals ({locationData.terminals.length})
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {locationData.terminals.map((terminalId) => (
                <Chip
                  key={terminalId}
                  label={terminalId}
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate(`/terminals/${terminalId}`)}
                  clickable
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        {/* Terminal Breakdown Section */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Terminal Breakdown
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Terminal ID</strong></TableCell>
                      <TableCell align="right"><strong>Transactions</strong></TableCell>
                      <TableCell align="right"><strong>Amount</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {locationData?.terminalBreakdown?.map((terminal, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Button
                            variant="text"
                            onClick={() => navigate(`/terminals/${terminal.TerminalID}`)}
                            sx={{ textTransform: 'none' }}
                          >
                            {terminal.TerminalID}
                          </Button>
                        </TableCell>
                        <TableCell align="right">{formatNumber(terminal.TransactionCount)}</TableCell>
                        <TableCell align="right">{formatCurrency(terminal.Amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Monthly Trend Section */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Monthly Trend
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Month</strong></TableCell>
                      <TableCell align="right"><strong>Transactions</strong></TableCell>
                      <TableCell align="right"><strong>Amount</strong></TableCell>
                      <TableCell align="right"><strong>Records</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {locationData?.monthlyTrend?.map((month, index) => (
                      <TableRow key={index}>
                        <TableCell>{month.Month}</TableCell>
                        <TableCell align="right">{formatNumber(month.TransactionCount)}</TableCell>
                        <TableCell align="right">{formatCurrency(month.Amount)}</TableCell>
                        <TableCell align="right">{formatNumber(month.RecordCount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

    </Box>
  );
};

export default LocationDetails;